import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

/**
 * Scrolls to the element matching the URL hash, subtracting the full
 * visual height of the fixed navbar (including the iOS safe-area-inset-top
 * which is baked into the nav via padding but NOT reflected in offsetHeight
 * when box-sizing:border-box is in use).
 *
 * env(safe-area-inset-top) is exposed as --safe-area-top in safe-areas.css so
 * we can read it from JS via getComputedStyle.
 */
export default function useScrollToHash(addNavHeight = false): null {
  const location = useLocation();
  const pendingHash = useRef("");

  useEffect(() => {
    if (location.hash) {
      pendingHash.current = location.hash.slice(1);
    }

    if (!pendingHash.current) return;

    const scrollToTarget = (attempt = 0) => {
      const target = document.getElementById(pendingHash.current);
      if (target) {
        let offset = 0;
        if (addNavHeight) {
          const nav = document.querySelector("nav");
          const navHeight = nav?.offsetHeight ?? 0;

          // Read safe-area-inset-top exposed as a CSS custom property.
          // On non-iOS devices this will be "0px" or empty.
          const safeAreaTop = parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--safe-area-top",
            ) || "0",
          );

          offset = navHeight + safeAreaTop;
        }

        requestAnimationFrame(() => {
          const top =
            target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: "smooth" });
          pendingHash.current = "";
        });
      } else if (attempt < 10) {
        setTimeout(() => scrollToTarget(attempt + 1), 300);
      } else {
        console.warn(`Element with id "${pendingHash.current}" not found`);
      }
    };

    scrollToTarget();
  }, [addNavHeight, location]);

  return null;
}
